import { z } from 'zod';

export const PART_POSITIONS = [
  'FRONT_LEFT',
  'FRONT_RIGHT',
  'REAR_LEFT',
  'REAR_RIGHT',
  'FRONT',
  'REAR',
  'ROOF',
  'OTHER',
] as const;

export type PartPosition = (typeof PART_POSITIONS)[number];

export const PartPositionSchema = z.enum(PART_POSITIONS);

function parseOptionalBoolean(value: unknown): boolean | undefined {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }
  if (value === true || value === 'true') {
    return true;
  }
  if (value === false || value === 'false') {
    return false;
  }
  return value as never;
}

export const RegisterVehiclePartBodySchema = z.object({
  vehiclePartTypeId: z.uuid(),
  partPosition: PartPositionSchema,
  customerVehicleId: z.uuid().optional(),
  condition: z.string().trim().optional(),
  notes: z.string().trim().optional(),
  confirmDuplicate: z.boolean().optional(),
});

export type RegisterVehiclePartBody = z.infer<typeof RegisterVehiclePartBodySchema>;

export const UpdateVehiclePartBodySchema = z.object({
  customerVehicleId: z.uuid().nullable().optional(),
  vehiclePartTypeId: z.uuid().optional(),
  partPosition: PartPositionSchema.optional(),
  condition: z.string().trim().nullable().optional(),
  notes: z.string().trim().nullable().optional(),
  confirmDuplicate: z.boolean().optional(),
});

export type UpdateVehiclePartBody = z.infer<typeof UpdateVehiclePartBodySchema>;

export const DeleteVehiclePartBodySchema = z.object({
  deleteReason: z.string().trim().optional(),
});

export type DeleteVehiclePartBody = z.infer<typeof DeleteVehiclePartBodySchema>;

export const ListPartyVehiclePartsQuerySchema = z.object({
  customerVehicleId: z.uuid().optional(),
  standaloneOnly: z.preprocess(parseOptionalBoolean, z.boolean().optional()),
});

export type ListPartyVehiclePartsQuery = z.infer<typeof ListPartyVehiclePartsQuerySchema>;

export const VehiclePartResponseSchema = z.object({
  id: z.uuid(),
  partyId: z.uuid(),
  customerVehicleId: z.uuid().nullable(),
  vehiclePartTypeId: z.uuid(),
  partPosition: PartPositionSchema,
  condition: z.string().nullable(),
  notes: z.string().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export type VehiclePartResponse = z.infer<typeof VehiclePartResponseSchema>;

export const ListPartyVehiclePartsResponseSchema = z.object({
  items: z.array(VehiclePartResponseSchema),
});

export type ListPartyVehiclePartsResponse = z.infer<typeof ListPartyVehiclePartsResponseSchema>;

export const RegisterVehiclePartResponseSchema = z.object({
  partId: z.uuid(),
});

export type RegisterVehiclePartResponse = z.infer<typeof RegisterVehiclePartResponseSchema>;

export const UpdateVehiclePartResponseSchema = z.object({
  partId: z.uuid(),
  changedFields: z.array(z.string()),
});

export type UpdateVehiclePartResponse = z.infer<typeof UpdateVehiclePartResponseSchema>;

export const VehiclePartTypeSchema = z.object({
  id: z.uuid(),
  slug: z.string(),
  name: z.string(),
});

export type VehiclePartType = z.infer<typeof VehiclePartTypeSchema>;

export const ListVehiclePartTypesResponseSchema = z.object({
  items: z.array(VehiclePartTypeSchema),
});

export type ListVehiclePartTypesResponse = z.infer<typeof ListVehiclePartTypesResponseSchema>;
